import SEO from "@/components/SEO";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/pages/home/components/Footer";
import { cursos as cursosMock } from "@/mocks/cursos";
import { useAuth } from "@/contexts/AuthContext";
import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";

const CURSOS_STORAGE_KEY = "gp_admin_cursos";

interface CursoItem {
  id: string;
  titulo: string;
  descricao: string;
  categoria: string;
  nivel: string;
  aulas: number;
  material: string;
  preco: number;
  imagem: string;
}

function getCursos(): CursoItem[] {
  const stored = localStorage.getItem(CURSOS_STORAGE_KEY);
  if (stored) {
    try {
      return JSON.parse(stored);
    } catch {
      return cursosMock.map((c) => ({ ...c }));
    }
  }
  return cursosMock.map((c) => ({ ...c }));
}

export default function CursosPage() {
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const [comprando, setComprando] = useState<string | null>(null);
  const [toast, setToast] = useState<{ type: "success" | "error"; msg: string } | null>(null);
  const [checkoutAddress, setCheckoutAddress] = useState<Record<string, string> | null>(null);
  const [cursos, setCursos] = useState<CursoItem[]>(getCursos());

  useEffect(() => {
    if (profile?.endereco && typeof profile.endereco === "object") {
      setCheckoutAddress(profile.endereco as Record<string, string>);
    }
  }, [profile]);

  // Sync from localStorage on mount and when storage changes
  useEffect(() => {
    const handleStorage = () => {
      setCursos(getCursos());
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  // Recarrega cursos quando a página volta ao foco (ex: admin editou em outra aba)
  useEffect(() => {
    const handleVis = () => {
      if (document.visibilityState === "visible") {
        setCursos(getCursos());
      }
    };
    document.addEventListener("visibilitychange", handleVis);
    return () => document.removeEventListener("visibilitychange", handleVis);
  }, []);

  // Recarrega cursos quando o foco volta à janela (alt-tab)
  useEffect(() => {
    const handleFocus = () => {
      setCursos(getCursos());
    };
    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, []);

  async function handleComprar(cursoId: string) {
    if (!user) {
      navigate(`/login?redirect=/cursos`);
      return;
    }

    const curso = cursos.find((c) => c.id === cursoId);
    if (!curso) {
      setToast({ type: "error", msg: "Curso não encontrado." });
      return;
    }

    setComprando(cursoId);
    try {
      const { data: produtos } = await supabase
        .from("product_items")
        .select("id, name")
        .eq("status", "active")
        .ilike("name", `%${curso.titulo}%`);

      const produto = produtos?.[0];
      if (!produto) {
        setToast({ type: "error", msg: "Curso ainda não disponivel para compra." });
        setComprando(null);
        return;
      }

      const { data, error } = await supabase.functions.invoke('create-stripe-checkout', {
        body: {
          items: [{ product_id: produto.id, quantity: 1 }],
          user_id: user.id,
          customer_email: user.email,
          address: checkoutAddress,
        },
      });
      if (error || !data || !data.url) {
        setToast({ type: "error", msg: data?.error || error?.message || "Erro ao iniciar pagamento." });
      } else {
        window.location.href = data.url;
      }
    } catch (err: any) {
      setToast({ type: "error", msg: err.message || "Erro ao processar compra." });
    } finally {
      setComprando(null);
    }
  }

  // Agrupar cursos por categoria
  const cursosPorCategoria = useMemo(() => {
    return cursos.reduce((acc, curso) => {
      const cat = curso.categoria || "Outros";
      if (!acc[cat]) acc[cat] = [];
      acc[cat].push(curso);
      return acc;
    }, {} as Record<string, CursoItem[]>);
  }, [cursos]);

  const ordemCategorias = ["Leitura e Teoria Musical", "Harmonia Musical", "Produção Musical"];
  const categorias = ordemCategorias.filter((c) => cursosPorCategoria[c]).concat(
    Object.keys(cursosPorCategoria).filter((c) => !ordemCategorias.includes(c))
  );

  const siteUrl = import.meta.env.VITE_SITE_URL || "https://example.com";
  const schema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "Cursos Online GodoyProd",
    url: `${siteUrl}/cursos`,
    description: "Cursos completos de teoria musical, leitura de partituras, harmonia e produção musical. Do iniciante ao avançado.",
    inLanguage: "pt-BR",
    itemListElement: cursos.map((c, i) => ({
      "@type": "ListItem",
      position: i + 1,
      item: {
        "@type": "Course",
        name: c.titulo,
        description: c.descricao,
        provider: {
          "@type": "Organization",
          name: "GodoyProd",
          url: siteUrl,
        },
        educationalLevel: c.nivel,
        courseMode: "online",
        inLanguage: "pt-BR",
      },
    })),
  };

  return (
    <>
      <SEO
        title="Cursos Online de Música"
        description="Cursos completos de teoria musical, leitura de partituras, harmonia e produção musical. Do iniciante ao avançado, com preços a partir de R$ 9,90."
        keywords="cursos de música, teoria musical, leitura de partituras, harmonia musical, produção musical, Sibelius, ProTools, Melodyne, Logic Pro"
        schema={schema}
        canonicalPath="/cursos"
      />
      <div className="min-h-screen bg-godoy-black">
        <Navbar />

        {/* Hero com background de piano/teclado */}
        <section className="relative pt-32 pb-20 md:pt-40 md:pb-28">
          <div className="absolute inset-0 bg-[url('https://readdy.ai/api/search-image?query=Elegant%20dark%20piano%20keyboard%20close-up%20soft%20golden%20ambient%20light%20musical%20keys%20luxury%20professional%20atmosphere%20abstract%20blurred%20background%20dark%20tones&width=1920&height=800&seq=godoy-cursos-hero&orientation=landscape')] bg-cover bg-center" />
          <div className="absolute inset-0 bg-godoy-black/80" />
          <div className="relative z-10 w-full px-6 md:px-10 lg:px-16 max-w-6xl mx-auto text-center">
            <span className="text-godoy-gold text-sm font-semibold tracking-widest uppercase">
              Educação Musical
            </span>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold text-godoy-cream mt-4">
              Cursos Online
            </h1>
            <p className="text-godoy-lightgray mt-4 max-w-2xl mx-auto">
              Cursos completos de teoria musical, leitura de partituras, harmonia e producao musical.
              Do iniciante ao avancado, com preços a partir de R$ 9,90 ate R$ 149,90.
            </p>
          </div>
        </section>

        <section className="w-full px-6 md:px-10 lg:px-16 pb-20 md:pb-28 space-y-20">
          <div className="max-w-6xl mx-auto">
            {categorias.map((categoria) => (
              <div key={categoria} className="mb-16">
                <div className="flex items-center gap-3 mb-8">
                  <div className="h-px flex-1 bg-godoy-gray/20" />
                  <h2 className="font-serif text-xl md:text-2xl font-bold text-godoy-gold whitespace-nowrap">
                    {categoria}
                  </h2>
                  <div className="h-px flex-1 bg-godoy-gray/20" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {cursosPorCategoria[categoria].map((curso) => (
                    <div
                      key={curso.id}
                      className="bg-godoy-charcoal/60 border border-godoy-gray/30 rounded-lg overflow-hidden hover:border-godoy-gold/40 transition-all group"
                    >
                      <div className="aspect-video overflow-hidden">
                        <img
                          src={curso.imagem}
                          alt={curso.titulo}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-6">
                        <div className="flex items-center gap-3 mb-3">
                          <span className="text-xs px-2.5 py-1 bg-godoy-gold/10 text-godoy-gold rounded font-medium">
                            {curso.nivel}
                          </span>
                          <span className="text-xs text-godoy-lightgray">
                            {curso.aulas} aulas
                          </span>
                        </div>
                        <h3 className="font-serif text-lg font-semibold text-godoy-cream mb-2">
                          {curso.titulo}
                        </h3>
                        <p className="text-sm text-godoy-lightgray leading-relaxed mb-4">
                          {curso.descricao}
                        </p>
                        <div className="flex items-center gap-2 text-xs text-godoy-lightgray mb-5">
                          <i className="ri-book-line text-godoy-gold" />
                          {curso.material}
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-godoy-gold font-bold text-lg">
                            R$ {curso.preco.toFixed(2).replace('.', ',')}
                          </span>
                          <button
                            onClick={() => handleComprar(curso.id)}
                            disabled={comprando === curso.id}
                            className="px-5 py-2 bg-godoy-gold text-godoy-black font-semibold rounded-md hover:bg-godoy-goldlight transition-all text-sm whitespace-nowrap disabled:opacity-60 flex items-center gap-2"
                          >
                            <i className="ri-shopping-cart-line" />
                            {comprando === curso.id ? "Processando..." : "Comprar"}
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            <div className="mt-16 bg-godoy-charcoal/40 border border-godoy-gray/20 rounded-lg p-8 md:p-12 text-center">
              <i className="ri-graduation-cap-line text-4xl text-godoy-gold/50 mb-4" />
              <h2 className="font-serif text-2xl md:text-3xl font-bold text-godoy-cream mb-3">
                Invista no Seu Conhecimento Musical
              </h2>
              <p className="text-godoy-lightgray max-w-2xl mx-auto">
                Nossos cursos sao elaborados por profissionais experientes e cobrem do nivel iniciante ao avancado.
                Acesse o conteudo completo com preços a partir de R$ 9,90 e transforme sua musicalidade.
              </p>
            </div>
          </div>
        </section>

        {/* Toast */}
        {toast && (
          <div className="fixed bottom-6 right-6 z-[150] max-w-sm">
            <div
              className={`px-5 py-3 rounded-md text-sm font-medium border ${
                toast.type === "success"
                  ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-400"
                  : "bg-red-500/10 border-red-500/30 text-red-400"
              }`}
            >
              <div className="flex items-center gap-2">
                <i className={toast.type === "success" ? "ri-check-line" : "ri-error-warning-line"} />
                {toast.msg}
                <button onClick={() => setToast(null)} className="ml-2 text-godoy-lightgray hover:text-godoy-cream">
                  <i className="ri-close-line" />
                </button>
              </div>
            </div>
          </div>
        )}

        <Footer />
      </div>
    </>
  );
}